// money.js — จุดเดียวของระบบสำหรับ "จำนวนเงิน"
// ---------------------------------------------------------------------------
// กฎของระบบ: จำนวนเงินทุกจุดต้องมีทศนิยม 2 ตำแหน่งเสมอ ทั้งตอนแสดงผลและตอนกรอก
// เพราะฐานข้อมูลเก็บเป็น DECIMAL(10,2) การโชว์ '207' ทั้งที่ DB เป็น '207.00'
// ทำให้เกิดปัญหาจริงมาแล้ว 2 แบบ:
//   1. แอดมินเห็นค่าไม่ตรงกันระหว่างช่องราคา ('6900.00') กับช่องค่าธรรมเนียม ('207')
//   2. ส่งค่าที่ไม่มีทศนิยมกลับไป → backend เทียบ '5344' กับ '5344.00' แบบสตริง
//      (ค่ามาจาก multipart จึงเป็นสตริงทั้งคู่) → มองว่า "เปลี่ยน" ทั้งที่เงินเท่าเดิม
//      → ขึ้น log ว่ามีการแก้ไข + เขียนทับซ้ำโดยไม่จำเป็น
//
// ล็อก locale เป็น 'en-US' ตายตัว ไม่ใช้ locale ของเครื่อง — ไม่งั้นเครื่องที่ตั้งเป็น
// เยอรมัน/ฝรั่งเศสจะได้ '1.234,56' ซึ่งอ่านผิดความหมายทันที และตรงกับ bahtText()
// ฝั่ง backend (server.js) ที่ใช้ 'en-US' เหมือนกัน ตัวเลขสองฝั่งจึงหน้าตาเดียวกัน
//
// ⚠️ ข้อยกเว้นเดียวที่ตั้งใจไม่ใช้ 2 ตำแหน่ง: ป้ายบนแท่งกราฟหน้ารายงานยอดขาย
//    (moneyShort ใน FinanceReport.vue — '12.5k' / '1.2M') เพราะพื้นที่บนแท่งไม่พอ
// ---------------------------------------------------------------------------

const MONEY_LOCALE = 'en-US';

// แสดงผลจำนวนเงิน: 207 -> '207.00', '3901.00' -> '3,901.00', null -> '0.00'
export function formatMoney(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return Number(0).toLocaleString(MONEY_LOCALE, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return n.toLocaleString(MONEY_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// แสดงผลพร้อมสัญลักษณ์บาทต่อท้าย: 207 -> '207.00 ฿'
export function formatBaht(v) {
  return formatMoney(v) + ' ฿';
}

// ค่าที่จะ "ใส่กลับเข้าช่องกรอก" — คืนสตริง 2 ตำแหน่งแบบไม่มีคอมมา (input type=number
// รับคอมมาไม่ได้ จะกลายเป็นช่องว่างทันที) และคืน '' เมื่อช่องว่าง เพื่อให้ล้างค่าได้จริง
// ใช้ตอน @blur ของทุกช่องเงิน + ตอนโหลดค่าเดิมเข้าฟอร์ม
export function normalizeMoneyInput(v) {
  if (v === null || v === undefined || String(v).trim() === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}

// เทียบจำนวนเงินสองค่าว่าเท่ากันไหม "ในระดับสตางค์" (คู่กับ sameMoney ฝั่ง backend)
// ใช้เวลาต้องรู้ว่าค่าที่ผู้ใช้แก้ ต่างจากค่าเดิมจริงหรือแค่รูปแบบทศนิยมต่างกัน
export function sameMoney(a, b) {
  const norm = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : Number(v));
  const na = norm(a);
  const nb = norm(b);
  if (na === null || nb === null) return na === nb;
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  return Math.round(na * 100) === Math.round(nb * 100);
}
